import Link from 'next/link'
import { PageHeader } from '@/components/layout/page-header'
import { ClipboardList, Clock, DollarSign, Package, Plus, Ship } from 'lucide-react'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/status-badge'
import { StatCard } from '@/components/ui/stat'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api/client'
import type {
  Consignment,
  Inquiry,
  SeaTrackingContainer,
  SeaTrackingShipment,
} from '@/lib/api/types'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { useOptimisticRows } from '@/src/hooks/useOptimisticRows'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'
import {
  ConsignmentQuickEdit,
  InquiryQuickEdit,
  QuickEditFailure,
} from '@/src/components/QuickEdit'
import { getConsignmentTrackingReference, shouldLoadSeaTracking } from '@/src/lib/tracking'
import { dashboardStats, loadOverview } from '@/src/lib/overview'

const NO_INQUIRIES: Inquiry[] = []
const NO_CONSIGNMENTS: Consignment[] = []

type People = ReturnType<typeof useHelmPeople>['data']

/** "Sara · due 24/10/2026": who has the record and when it is due, for a recent row. */
function ownerAndDue(
  record: { owner_user_id?: string | null; sla_due_at?: string | null },
  people: People,
) {
  const owner = !record.owner_user_id
    ? 'Unassigned'
    : record.owner_user_id === people?.meId
      ? 'You'
      : personName(people?.users.get(record.owner_user_id))
  return record.sla_due_at
    ? `${owner} · due ${new Date(record.sla_due_at).toLocaleDateString()}`
    : owner
}

const compactNumber = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

type SeaTrackingDashboardItem = {
  consignment: Consignment
  reference: string
  shipment: SeaTrackingShipment | null
  containers: SeaTrackingContainer[]
  error: string | null
}

async function loadDashboardData() {
  const [consignments, inquiries, overview] = await Promise.all([
    apiClient.listConsignments(),
    apiClient.listInquiries().catch(() => [] as Inquiry[]),
    loadOverview(),
  ])
  const seaCandidates = consignments.filter(shouldLoadSeaTracking).slice(0, 5)

  const seaTracking = await Promise.all(
    seaCandidates.map(async (consignment): Promise<SeaTrackingDashboardItem> => {
      const reference =
        getConsignmentTrackingReference(consignment) || `Consignment ${consignment.id.slice(0, 8)}`

      try {
        const [shipment, containerPage] = await Promise.all([
          apiClient.getSeaTrackingShipment(reference),
          apiClient.listSeaTrackingContainers(reference, 1, 10),
        ])

        return {
          consignment,
          reference,
          shipment,
          containers: containerPage.containers,
          error: null,
        }
      } catch (error) {
        return {
          consignment,
          reference,
          shipment: null,
          containers: [],
          error: error instanceof Error ? error.message : 'Sea tracking lookup failed',
        }
      }
    }),
  )

  return { consignments, inquiries, seaTracking, overview }
}

export function DashboardPage() {
  const { data, error, loading, reload } = useAsyncData(loadDashboardData, [])
  const people = useHelmPeople()
  const canEdit = Boolean(people.data?.canEdit)
  // Quick edits show on the row before the API answers.
  const inquiryEdits = useOptimisticRows(data?.inquiries ?? NO_INQUIRIES)
  const consignmentEdits = useOptimisticRows(data?.consignments ?? NO_CONSIGNMENTS)

  if (loading) return <LoadingState label="Loading dashboard..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const safeConsignments = data?.consignments ?? []
  const safeInquiries = data?.inquiries ?? []
  const seaTrackingItems = data?.seaTracking ?? []
  // The lists are the first page only; counts come from /overview.
  const stats = dashboardStats(data?.overview ?? null, safeConsignments, safeInquiries)
  const recentConsignments = consignmentEdits.rows.slice(0, 5)
  // New and active requests: a cancelled one is not either.
  const recentInquiries = inquiryEdits.rows
    .filter((inquiry) => inquiry.operational_state !== 'CANCELLED')
    .slice(0, 5)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Welcome back. Here is your import overview."
        actions={
          <Link href="/inquiries/new">
            <Button variant="brand">+ New Inquiry</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard
          label="Total Consignments"
          icon={Package}
          value={stats.totalConsignments}
          description="All active records"
        />
        <StatCard
          label="Open Inquiries"
          icon={ClipboardList}
          value={stats.openInquiries}
          description="Not completed or cancelled"
        />
        <StatCard
          label="Pending Estimates"
          icon={Clock}
          value={stats.pendingEstimates}
          description="No tax estimate yet"
        />
        <StatCard
          label="Sea Tracking"
          icon={Ship}
          value={stats.seaTracked}
          description="Container-linked records"
        />
        <StatCard
          // A currency figure needs the full width of a phone.
          className="col-span-2 lg:col-span-1"
          label="Total Tax Estimated"
          icon={DollarSign}
          // Shillings run to eight digits; compact keeps it whole on the card.
          value={`UGX ${compactNumber.format(stats.totalTaxEstimated)}`}
          title={`UGX ${stats.totalTaxEstimated.toLocaleString()}`}
          description="All time"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Inquiries</CardTitle>
          <CardDescription>New and active service requests.</CardDescription>
          <CardAction>
            <Button asChild variant="outline" size="sm">
              <Link href="/inquiries/new">
                <Plus />
                New inquiry
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-3">
          <QuickEditFailure edits={inquiryEdits} />
          {recentInquiries.length > 0 ? (
            <div className="space-y-3">
              {recentInquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="flex items-center gap-1 rounded-md border pr-1 transition-colors hover:bg-muted/50"
                >
                  <Link
                    href={`/inquiries/${inquiry.id}`}
                    className="flex min-w-0 flex-1 flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">
                        {inquiry.customer?.company_name ||
                          inquiry.customer?.name ||
                          `Inquiry #${inquiry.id.slice(0, 8)}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatInquiryServices(inquiry)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ownerAndDue(inquiry, people.data)}
                      </p>
                    </div>
                    <div className="sm:text-right">
                      <StatusBadge status={inquiry.status} />
                      <p className="mt-1 text-sm text-muted-foreground">
                        {inquiry.next_action || 'No next action'}
                      </p>
                    </div>
                  </Link>
                  {canEdit && <InquiryQuickEdit inquiry={inquiry} edits={inquiryEdits} />}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">No inquiries yet.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sea Tracking Command Center</CardTitle>
          <CardDescription>
            Recent sea/container consignments with live tracking lookup.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {seaTrackingItems.length > 0 ? (
            <div className="space-y-3">
              {seaTrackingItems.map((item) => {
                const status =
                  item.shipment?.process_status || item.shipment?.data_status || 'Pending'
                const movement = describeContainerMovement(item.containers[0])
                const route = describeContainerRoute(item.containers[0])
                const carrier = getCarrierCode(item.shipment, item.containers[0])

                return (
                  <div
                    key={item.consignment.id}
                    className="grid gap-3 rounded-md border p-3 md:grid-cols-[1fr_auto]"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          href={`/consignments/${item.consignment.id}/tracking`}
                          className="font-medium hover:underline"
                        >
                          {item.reference}
                        </Link>
                        <Badge variant={item.error ? 'destructive' : 'secondary'}>
                          {item.error ? 'Lookup Failed' : formatLabel(status)}
                        </Badge>
                        <Badge variant="outline">{carrier}</Badge>
                        {item.shipment?.cached && <Badge variant="outline">Cached</Badge>}
                      </div>
                      <p className="mt-1 truncate text-sm text-muted-foreground">
                        {item.consignment.customer?.company_name ||
                          item.consignment.customer?.name ||
                          item.consignment.destination_port}
                      </p>
                      {route && <p className="mt-1 text-sm text-muted-foreground">{route}</p>}
                      <p className="mt-1 text-sm">{item.error || movement}</p>
                    </div>
                    <div className="flex items-center gap-3 md:justify-end">
                      <div className="text-right text-sm">
                        <p className="font-medium">
                          {item.containers.length.toLocaleString()} containers
                        </p>
                        <p className="text-muted-foreground">
                          {formatDateTime(
                            item.shipment?.last_synced_at ||
                              item.shipment?.last_successful_tracking_at,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No sea/container consignments with tracking references yet.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Consignments</CardTitle>
          <CardDescription>Your latest import activities</CardDescription>
          <CardAction>
            <Button asChild variant="outline" size="sm">
              <Link href="/consignments/new">
                <Plus />
                New consignment
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-3">
          <QuickEditFailure edits={consignmentEdits} />
          {recentConsignments.length > 0 ? (
            <div className="space-y-3">
              {recentConsignments.map((consignment) => (
                <div
                  key={consignment.id}
                  className="flex items-center gap-1 rounded-md border pr-1 transition-colors hover:bg-muted/50"
                >
                  <Link
                    href={`/consignments/${consignment.id}`}
                    className="flex min-w-0 flex-1 flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                  >
                    {/* Named by its code and title: a tracking number is only small print. */}
                    <div className="min-w-0">
                      {consignment.code && (
                        <p className="text-xs text-muted-foreground">{consignment.code}</p>
                      )}
                      <p className="font-medium break-words">
                        {consignment.title || `Consignment #${consignment.id.slice(0, 8)}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {[
                          consignment.customer?.company_name || consignment.customer?.name,
                          consignment.destination_port,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ownerAndDue(consignment, people.data)}
                      </p>
                      {getConsignmentTrackingReference(consignment) && (
                        <p className="text-xs break-all text-muted-foreground/80">
                          Tracking {getConsignmentTrackingReference(consignment)}
                        </p>
                      )}
                    </div>
                    <div className="sm:text-right">
                      <StatusBadge status={consignment.status} />
                      <p className="text-sm text-muted-foreground">
                        UGX {(consignment.total_tax_estimate ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </Link>
                  {canEdit && (
                    <ConsignmentQuickEdit consignment={consignment} edits={consignmentEdits} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No consignments yet. Create your first one to get started.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatInquiryServices(inquiry: Inquiry) {
  const services = inquiry.service_types?.length ? inquiry.service_types : [inquiry.service_type]
  return services.map(formatLabel).join(', ')
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not synced'
  return new Date(value).toLocaleString()
}

function describeContainerMovement(container: SeaTrackingContainer | undefined) {
  if (!container) return 'No container movement has been returned yet.'
  const movement = container.last_movement
  const event =
    readString(movement, 'event') ||
    readString(movement, 'status') ||
    readString(movement, 'description')
  const location =
    formatLocationName(movement) || container.pod_terminal_name || formatLocationName(container.pod)
  const vessel = [container.arriving_vessel_name, container.arriving_vessel_voyage_no]
    .filter(Boolean)
    .join(' ')

  return (
    [
      event ? formatLabel(event) : null,
      location ? `at ${location}` : null,
      vessel ? `via ${vessel}` : null,
    ]
      .filter(Boolean)
      .join(' ') || 'Container movement is available from the sea tracking feed.'
  )
}

function describeContainerRoute(container: SeaTrackingContainer | undefined) {
  if (!container) return null
  const origin = formatLocationName(container.pol) || formatLocationName(container.pre)
  const destination =
    container.pod_terminal_name ||
    formatLocationName(container.pod) ||
    formatLocationName(container.pde)
  if (!origin && !destination) return null
  return [origin || 'Origin pending', destination || 'Destination pending'].join(' to ')
}

function readString(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'string' && entry.trim() ? entry.trim() : null
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function formatLocationName(value: Record<string, unknown> | null | undefined) {
  const record = readRecord(value)
  if (!record) return null
  const nestedLocation =
    readRecord(record.location) ?? readRecord(record.port) ?? readRecord(record.terminal)
  return (
    readString(record, 'location') ||
    readString(record, 'location_name') ||
    readString(record, 'port_name') ||
    readString(record, 'terminal_name') ||
    readString(record, 'name') ||
    readString(record, 'unlocode') ||
    readString(nestedLocation, 'name') ||
    readString(nestedLocation, 'port_name') ||
    readString(nestedLocation, 'terminal_name') ||
    readString(nestedLocation, 'unlocode')
  )
}

function getCarrierCode(
  shipment: SeaTrackingShipment | null | undefined,
  container: SeaTrackingContainer | undefined,
) {
  const rawCarrier =
    shipment?.carrier_scac ||
    readString(shipment?.raw, 'carrier_scac') ||
    readString(shipment?.raw, 'carrier_name') ||
    readString(container?.raw, 'carrier_scac') ||
    readString(container?.raw, 'carrier_name') ||
    ''
  const normalized = rawCarrier.toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (normalized.includes('MAERSK') || normalized.includes('MAEU')) return 'MAEU'
  if (normalized.includes('PIL') || normalized.includes('PCIU')) return 'PIL'
  if (normalized.includes('MSCU') || normalized.includes('MSC')) return 'MSC'
  if (normalized.includes('CMA') || normalized.includes('CGM')) return 'CMA CGM'
  if (normalized.includes('COSCO') || normalized.includes('COSU')) return 'COSCO'
  if (normalized.includes('ONE') || normalized.includes('ONEY')) return 'ONE'
  if (rawCarrier) return rawCarrier.toUpperCase()
  return 'SEA'
}
